import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Check, ArrowRight, AlertTriangle } from "lucide-react";
import { Stage } from "@/components/Stage";
import { Eyebrow } from "@/components/Wordmark";

const EASE = [0.22, 1, 0.36, 1];

const STAGES = [
  {
    n: 1,
    name: "Not started",
    tag: "No AI in production",
    summary: "Everything runs on staff time. Leadership sees the relevance but has no starting point.",
    looks: [
      "Intake, follow-up and case tracking are all manual.",
      "Informal, unmanaged ChatGPT use with no firm policy.",
    ],
    human: "No agent to work alongside yet. The human job is upstream — naming the repetitive, high-volume workflows worth automating.",
    move: [
      "Name one high-volume, rules-based workflow (start with intake).",
      "Commit to a single pilot for it.",
      "Define 3–4 success metrics before launch — not after.",
    ],
    moveLabel: "Next move — get one pilot live",
  },
  {
    n: 2,
    name: "Piloting",
    tag: "One workflow live",
    summary: "One workflow — usually intake — is live on AI. Early wins, but contained to one team.",
    looks: [
      "Faster response times, fewer missed contacts.",
      "The tool hasn't spread past the team that adopted it.",
    ],
    human: "The agent handles a task end-to-end; a human reviews its output before it reaches a client. It's treated like a new hire you check and correct.",
    move: [
      "Name a sponsor with authority to expand the pilot.",
      "Pick a second workflow for leverage, not novelty.",
    ],
    moveLabel: "Next move — expand with an owner",
    divergence: "Single-event: records follow-up or treatment-status checks. Mass tort / MDL: claimant intake at volume, batch across a cohort.",
  },
  {
    n: 3,
    name: "Multiple, disconnected",
    tag: "4–5 tools, no glue",
    summary: "AI runs across several workflows — but on separate tools, data and owners. Wins can't be added up.",
    looks: [
      "Staff re-key data between disconnected systems.",
      "Leadership can name individual wins, not combined impact.",
    ],
    human: "Each agent owns a slice of a workflow. Less line-by-line review — but humans become the bridges, manually reconciling outputs between systems.",
    move: [
      "Wire the tools into a single system of record.",
      "Make data flow automatically between workflows.",
      "Put KPIs in writing: response time, accuracy, escalation rate.",
    ],
    moveLabel: "Next move — integrate & assign ownership",
    divergence: "Single-event PI: Litify, Filevine or Clio. Mass tort / MDL: SmartAdvocate.",
  },
  {
    n: 4,
    name: "Connected & compounding",
    tag: "One dataset, predictive",
    summary: "Every workflow shares one dataset in the core system. Clean data unlocks predictive AI.",
    looks: [
      "Leadership reviews consistent KPIs on a set cadence.",
      "AI flags cases falling behind and forecasts settlements.",
    ],
    human: "Agents act like employees with roles and track records. Humans manage by exception — attention goes only to what AI flags as unusual.",
    move: [
      "Nothing structural left to add.",
      "Keep the discipline: pick a problem, integrate, measure — and compound.",
    ],
    moveLabel: "In practice — compounding gains",
    divergence: "KPIs compound: % triaged with no escalation, trigger-to-action time, capacity freed vs. baseline, override rate trending down.",
  },
];

const StageNode = ({ stage, i, selected, reached, onSelect }) => {
  const active = i === selected;
  return (
    <button
      type="button"
      data-testid={`maturity-stage-${stage.n}`}
      aria-pressed={active}
      onClick={() => onSelect(i)}
      className="group relative z-10 flex flex-col items-center flex-1 min-w-0 outline-none"
    >
      <span
        className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-300 shrink-0 ${
          active
            ? "bg-accent border-accent text-white scale-110 shadow-[0_8px_20px_-6px_rgba(30,91,255,0.5)]"
            : reached
            ? "bg-[#E7EEFF] border-accent text-accent"
            : "bg-[#F2F1EF] border-hairline text-ink-muted group-hover:border-ink-soft"
        }`}
      >
        {reached && !active ? (
          <Check className="w-4 h-4" strokeWidth={3} />
        ) : (
          <span className="font-mono text-sm font-medium tabular-nums">{stage.n}</span>
        )}
      </span>
      <span
        className={`mt-3 font-sans text-[13px] font-semibold leading-tight text-center transition-colors duration-300 ${
          active ? "text-ink" : "text-ink-soft group-hover:text-ink"
        }`}
      >
        {stage.name}
      </span>
      <span className="mt-0.5 font-mono text-[9px] uppercase tracking-[0.1em] text-ink-muted text-center leading-tight">
        {stage.tag}
      </span>
    </button>
  );
};

export const MaturitySlide = ({ slide }) => {
  const reduce = useReducedMotion();
  const [selected, setSelected] = useState(1);
  const cur = STAGES[selected];
  const progressPct = (selected / (STAGES.length - 1)) * 100;
  const showStall = selected === 1 || selected === 2;

  const container = {
    hidden: {},
    show: { transition: { staggerChildren: reduce ? 0 : 0.1, delayChildren: 0.05 } },
  };
  const item = {
    hidden: { opacity: 0, y: reduce ? 0 : 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
  };

  return (
    <Stage testId={`slide-${slide?.n ?? 2}`}>
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="h-full w-full flex flex-col px-[clamp(28px,6%,84px)] py-[clamp(22px,4.5%,48px)]"
      >
        {/* Top row */}
        <motion.div variants={item} className="flex items-center justify-between">
          <Eyebrow>Where you are today</Eyebrow>
          <span className="font-mono text-xs tracking-[0.24em] text-ink-muted tabular-nums">
            02 / 11
          </span>
        </motion.div>

        {/* Headline */}
        <motion.h1
          variants={item}
          className="font-display font-medium text-3xl sm:text-4xl leading-[1.06] text-ink mt-4 max-w-[20ch]"
        >
          The AI maturity curve for plaintiffs' firms.
        </motion.h1>

        <motion.p
          variants={item}
          className="font-sans text-[14px] text-ink-soft leading-relaxed mt-3 max-w-[64ch]"
        >
          Value doesn't come from sophistication — it comes from knowing your
          stage and the one next move.{" "}
          <span className="text-accent">Tap a stage to place your firm.</span>
        </motion.p>

        {/* Stepper */}
        <motion.div variants={item} className="relative mt-9 mb-2 px-2">
          {/* track (sits behind the opaque nodes, only visible in the gaps) */}
          <div className="absolute left-[12.5%] right-[12.5%] top-5 h-[3px] rounded-full bg-hairline -translate-y-1/2" />
          <div
            className="absolute left-[12.5%] top-5 h-[3px] rounded-full bg-accent -translate-y-1/2 transition-[width] duration-500"
            style={{ width: `calc((100% - 25%) * ${progressPct / 100})` }}
          />
          {/* stall marker between stage 2 and 3 */}
          <div className="absolute top-5 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[5] flex flex-col items-center">
            <span className="w-3 h-3 rounded-full bg-[#F7F6F5] border-2 border-[#B98900] flex items-center justify-center">
              <span className="w-1 h-1 rounded-full bg-[#B98900]" />
            </span>
          </div>
          <div className="relative flex items-start justify-between gap-1">
            {STAGES.map((s, i) => (
              <StageNode
                key={s.n}
                stage={s}
                i={i}
                selected={selected}
                reached={i < selected}
                onSelect={setSelected}
              />
            ))}
          </div>
        </motion.div>

        {/* Detail */}
        <motion.div
          variants={item}
          className="flex-1 min-h-0 grid grid-cols-1 min-[820px]:grid-cols-[1fr_0.92fr] gap-4 mt-5"
        >
          {/* left — current state */}
          <motion.div
            key={`left-${selected}`}
            initial={{ opacity: 0, y: reduce ? 0 : 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: reduce ? 0.001 : 0.4, ease: EASE }}
            data-testid="maturity-detail"
            className="rounded-2xl border border-hairline bg-[#FBFAF6] p-5 flex flex-col"
          >
            <div className="flex items-center gap-3">
              <span className="font-mono text-sm text-accent tabular-nums">
                {String(cur.n).padStart(2, "0")}
              </span>
              <h3 className="font-display font-medium text-xl text-ink leading-tight">
                {cur.name}
              </h3>
            </div>
            <p className="font-sans text-[14px] text-ink-soft leading-relaxed mt-2.5">
              {cur.summary}
            </p>

            <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-ink-muted mt-4 mb-2">
              What it looks like
            </div>
            <div className="space-y-2">
              {cur.looks.map((l, k) => (
                <div key={k} className="flex items-start gap-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent mt-[7px] shrink-0" />
                  <span className="font-sans text-[13px] text-ink-soft leading-snug">
                    {l}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-auto pt-4 border-t border-hairline">
              <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-ink-muted mb-1.5">
                Human + AI
              </div>
              <p className="font-sans text-[13px] text-ink-soft leading-snug">
                {cur.human}
              </p>
            </div>
          </motion.div>

          {/* right — next move (dark) */}
          <motion.div
            key={`right-${selected}`}
            initial={{ opacity: 0, y: reduce ? 0 : 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: reduce ? 0.001 : 0.45, ease: EASE }}
            data-testid="maturity-next-move"
            className="rounded-2xl p-5 flex flex-col text-paper bg-[linear-gradient(155deg,#191622_0%,#211D2A_100%)]"
          >
            <span className="inline-flex items-center gap-2 self-start rounded-full bg-white/[0.06] border border-white/10 pl-1 pr-3 py-1">
              <span className="w-5 h-5 rounded-full bg-accent flex items-center justify-center shrink-0">
                <ArrowRight className="w-3 h-3 text-white" strokeWidth={2.5} />
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#93B4FF]">
                {cur.moveLabel}
              </span>
            </span>

            <div className="mt-4 space-y-2.5">
              {cur.move.map((m, k) => (
                <div key={k} className="flex items-start gap-2.5">
                  <span className="font-mono text-[11px] text-[#93B4FF] mt-[1px] shrink-0 tabular-nums">
                    {String(k + 1).padStart(2, "0")}
                  </span>
                  <span className="font-sans text-[13px] text-paper/85 leading-snug">
                    {m}
                  </span>
                </div>
              ))}
            </div>

            {cur.divergence ? (
              <div className="mt-auto pt-4">
                <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
                  <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-paper/45 mb-1.5">
                    {selected === 3 ? "KPIs that compound" : "System of record"}
                  </div>
                  <p className="font-sans text-[12px] text-paper/70 leading-snug">
                    {cur.divergence}
                  </p>
                </div>
              </div>
            ) : null}
          </motion.div>
        </motion.div>

        {/* Stall point callout */}
        <motion.div
          variants={item}
          data-testid="maturity-stall-note"
          className={`mt-4 flex items-center gap-2.5 rounded-xl px-4 py-2.5 border transition-all duration-300 ${
            showStall
              ? "border-[#B98900]/30 bg-[#B98900]/[0.07]"
              : "border-hairline bg-transparent"
          }`}
        >
          <AlertTriangle
            className={`w-4 h-4 shrink-0 ${showStall ? "text-[#B98900]" : "text-ink-muted"}`}
          />
          <p className="font-sans text-[13px] text-ink-soft leading-snug">
            <span className="font-semibold text-ink">Most firms stall between Stage 2 and 3.</span>{" "}
            They prove a pilot works but never name an owner to drive integration — so
            they collect disconnected tools instead of a compounding system. The fix is{" "}
            <span className="text-accent">integration and ownership, not more tools.</span>
          </p>
        </motion.div>
      </motion.div>
    </Stage>
  );
};

export default MaturitySlide;
